import React, { useState, useEffect } from 'react';
import { withImagePlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

const DEFAULT_IMAGE_CLASS = 'w-full h-52 sm:h-64 md:h-80 lg:h-96 object-cover object-center';

/**
 * Cover image for listing detail pages (mobile through desktop).
 *
 * `className` styles the frame (rounding, ring, width); `imageClassName` overrides
 * the image box itself so a page can use a shorter cover without affecting the
 * other listing types that share this component.
 *
 * Degrades gracefully: if the resolved cover URL is non-empty but fails to load
 * (e.g. a listing whose stored image URL is broken), the cover falls back to the
 * shared placeholder instead of rendering a broken image. This mirrors how
 * LazyImage already behaves, so covers stay consistent across the app.
 */
const ListingCoverHero = ({
  src,
  alt = '',
  className = '',
  imageClassName = DEFAULT_IMAGE_CLASS,
  onClick,
}) => {
  const resolvedSrc = withImagePlaceholder(src);
  const [displaySrc, setDisplaySrc] = useState(resolvedSrc);

  // Reset when the incoming source changes (e.g. navigating between listings).
  useEffect(() => {
    setDisplaySrc(resolvedSrc);
  }, [resolvedSrc]);

  const handleError = () => {
    if (displaySrc !== IMAGE_PLACEHOLDER_PATH) {
      setDisplaySrc(IMAGE_PLACEHOLDER_PATH);
    }
  };

  const image = (
    <img
      src={displaySrc}
      alt={alt}
      onError={handleError}
      className={imageClassName}
      loading="eager"
    />
  );

  if (!onClick) {
    return (
      <div className={`w-full bg-gray-200 overflow-hidden ${className}`.trim()}>
        {image}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full bg-gray-200 overflow-hidden group cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 ${className}`.trim()}
      aria-label={`View full size: ${alt || 'cover image'}`}
    >
      {image}
      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
        <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-3 py-1 rounded">
          View full size
        </span>
      </span>
    </button>
  );
};

export default ListingCoverHero;
